import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';

import { DEFAULT_LANGUAGE_CODE } from '../../utils/language';
import { fetchCasinoConfigAction } from '../apiActions/fetchCasinoConfigAction';
import { fetchPlayerConfigSyncAction } from '../apiActions/fetchPlayerConfigSyncAction';
import { fetchTableConfigAction } from '../apiActions/fetchTableConfigAction';
import type { CoreRootState } from '../CoreStore';
import { subtitlesActions } from '../slices/subtitles/slice';
import { SubtitlesGroup } from '../slices/subtitles/types';

type SyncActionType =
  | typeof subtitlesActions.finishOnboarding.type
  | typeof subtitlesActions.setIsEnabled.type
  | typeof subtitlesActions.setPositions.type
  | typeof subtitlesActions.setSize.type;

export const subtitlesSyncMiddleware = createListenerMiddleware<CoreRootState>();

// Listener to initialize subtitles group
subtitlesSyncMiddleware.startListening({
  matcher: isAnyOf(fetchCasinoConfigAction.fulfilled, fetchTableConfigAction.fulfilled),
  effect: (_, { dispatch, getState }) => {
    const {
      config: { casinoConfig, tableConfig, session },
    } = getState();

    if (!(casinoConfig && tableConfig && session)) {
      return;
    }

    const dealerLanguage = (tableConfig.dedicatedLanguage || DEFAULT_LANGUAGE_CODE).toLowerCase();
    const playerLanguage = (session.lang || DEFAULT_LANGUAGE_CODE).toLowerCase();
    const enabledLanguages = tableConfig?.subtitle ?? [];
    let subtitlesGroup: SubtitlesGroup;
    if (dealerLanguage === playerLanguage) {
      subtitlesGroup = SubtitlesGroup.GROUP1;
    } else if (enabledLanguages.includes(playerLanguage)) {
      subtitlesGroup = SubtitlesGroup.GROUP2;
    } else {
      subtitlesGroup = SubtitlesGroup.GROUP3;
    }

    dispatch(subtitlesActions.setGroup(subtitlesGroup));
  },
});

// Listener to save subtitles config to the player config when the subtitles config changes
subtitlesSyncMiddleware.startListening({
  // List of actions that trigger this middleware to send POST messages to the server
  matcher: isAnyOf(
    subtitlesActions.finishOnboarding,
    subtitlesActions.setIsEnabled,
    subtitlesActions.setPositions,
    subtitlesActions.setSize
  ),
  effect: (action, { dispatch, getState }) => {
    const state = getState();
    const session = state.config.session;
    if (!session) return;

    const { JSESSIONID, metaServer: url, gametype } = session;
    if (!(JSESSIONID && url)) return;

    const body = generateBody(action.type as SyncActionType, state);
    const timeStamp = Date.now();

    dispatch(
      fetchPlayerConfigSyncAction({
        JSESSIONID,
        ck: timeStamp.toString(),
        url: `${url}/api/ui/savePlayerConfigGeneric`,
        body: { ...body, gameType: gametype ?? '' },
      })
    );
  },
});

const generateBody = (actionType: SyncActionType, state: CoreRootState): Record<string, unknown> => {
  const { subtitles } = state;

  switch (actionType) {
    case subtitlesActions.finishOnboarding.type: {
      return { sub_onboarded: subtitles.isOnboarded };
    }
    case subtitlesActions.setIsEnabled.type: {
      return { [`sub_enabled_${subtitles.group.toString()}`]: subtitles.isEnabled };
    }
    case subtitlesActions.setPositions.type: {
      return { sub_positions: JSON.stringify(subtitles.positionsRaw) };
    }
    case subtitlesActions.setSize.type: {
      return { sub_sizes: JSON.stringify(subtitles.sizes) };
    }
    default: {
      return {};
    }
  }
};
